/**
 * Schreibmodus mit echtem Save/Load via API + Auto-Save.
 * iPad-kompatibel via Standard-Textarea (Apple Scribble nutzbar).
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Save, Printer, Trash2, Volume2, Check } from 'lucide-react';
import { documentsApi, topicsApi } from '@/lib/api';
import { useSpeak } from '@/lib/audio';
import { getCurrentWordPrefix, isSentenceStart, predictWords } from '@/lib/predict';
import { useSettings } from '@/lib/settings';
import { readDraft, useDocAutosave } from '@/lib/doc-autosave';
import { printWithTip } from '@/lib/print';
import { useValidActiveProfile } from '@/lib/use-valid-profile';
import { CorrectionNote } from '@/features/documents/correction-note';
import { Button } from '@/components/ui/button';
import { AuthGuard } from '@/lib/auth-guard';
import { PrintHeader } from '@/components/print-header';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/schreiben')({
  validateSearch: (s) => ({ doc: (s.doc as string | undefined) ?? undefined }),
  component: () => (
    <AuthGuard>
      <WriteScreen />
    </AuthGuard>
  ),
});

/**
 * Großbuchstabe am Satzanfang. Macht den ersten Klein-Buchstaben
 * eines neuen Satzes zu seinem Großbuchstaben — aber nur die NEU
 * eingetippten Stellen, damit der Cursor nicht verschoben wird und
 * frühere Wörter im Text nicht ungefragt umgeschrieben werden.
 *
 * Trigger:
 *   - allererster Buchstabe im Text
 *   - Buchstabe direkt nach . ! ? (+ Leerzeichen)
 *   - Buchstabe direkt nach einem Zeilenumbruch (neuer Absatz)
 */
function autoCapitalize(prev: string, next: string): string {
  if (next.length <= prev.length) return next; // Löschen → nichts tun
  // Erste Divergenz finden — Auto-Cap nur ab da, damit alte Wörter ruhe haben.
  let diff = 0;
  while (diff < prev.length && diff < next.length && prev[diff] === next[diff]) diff++;

  const out: string[] = [];
  for (let i = 0; i < next.length; i++) {
    const ch = next[i]!;
    if (i < diff) {
      out.push(ch);
      continue;
    }
    if (!/[a-zäöüß]/.test(ch)) {
      out.push(ch);
      continue;
    }
    // Schau zurück über whitespaces hinweg; merke dir aber, ob
    // ein Zeilenumbruch dabei war (Newline triggert auch Satzanfang).
    let j = i - 1;
    let sawNewline = false;
    while (j >= 0 && /\s/.test(out[j] ?? next[j]!)) {
      if ((out[j] ?? next[j]!) === '\n') sawNewline = true;
      j--;
    }
    const prevCh = j >= 0 ? out[j] ?? next[j]! : null;
    const isSentenceStart =
      prevCh === null ||
      prevCh === '.' ||
      prevCh === '!' ||
      prevCh === '?' ||
      sawNewline;
    out.push(isSentenceStart ? ch.toUpperCase() : ch);
  }
  return out.join('');
}

function WriteScreen() {
  const speak = useSpeak();
  const settings = useSettings();
  const { activeProfileId } = useValidActiveProfile();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);

  const [text, setText] = useState('');
  const [cursor, setCursor] = useState(0);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [docId, setDocId] = useState<string | null>(search.doc ?? null);

  const { data: topics = [] } = useQuery({
    queryKey: ['topics'],
    queryFn: () => topicsApi.list(),
  });

  // Existierenden Doc laden
  const { data: loadedDoc } = useQuery({
    queryKey: ['document', docId],
    queryFn: () => documentsApi.get(docId!),
    enabled: !!docId,
  });

  // Speichern (Auto-Speichern, Nachspeichern beim Verlassen, Entwurf auf dem Gerät)
  const saver = useDocAutosave({
    type: 'text',
    profileId: activeProfileId,
    text,
    docId,
    onDocId: setDocId,
    loadedContent: loadedDoc?.content ?? null,
    autoSave: settings.autoSave,
    minLength: 5,
  });

  useEffect(() => {
    if (!loadedDoc) return;
    // Gerade selbst angelegt → Editor nicht mit dem Server-Stand überschreiben
    // (sonst gingen Buchstaben verloren, die während des Speicherns getippt wurden).
    if (saver.isOwnDoc(loadedDoc.id)) return;
    // Gibt es auf diesem Gerät einen neueren, ungespeicherten Stand dieses Textes?
    const draft = readDraft('text', activeProfileId);
    const useDraft =
      draft?.docId === loadedDoc.id && draft.at > new Date(loadedDoc.updatedAt).getTime() && draft.content !== loadedDoc.content;
    const content = useDraft ? draft!.content : loadedDoc.content;
    setText(content);
    setCursor(content.length);
    saver.setLastSaved(new Date(loadedDoc.updatedAt));
    if (useDraft) toast.info('Nicht gespeicherte Änderungen wiederhergestellt.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedDoc]);

  // Neuer Text: ungespeicherten Entwurf von vorhin zurückholen (z.B. nach abgelaufener Anmeldung)
  useEffect(() => {
    if (search.doc) return;
    const draft = readDraft('text', activeProfileId);
    if (!draft) return;
    setText(draft.content);
    setCursor(draft.content.length);
    if (draft.docId) setDocId(draft.docId);
    toast.info('Nicht gespeicherter Text wiederhergestellt.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  useLayoutEffect(() => {
    const pos = pendingCaretRef.current;
    if (pos !== null && editorRef.current) {
      editorRef.current.setSelectionRange(pos, pos);
      pendingCaretRef.current = null;
    }
  }, [text]);

  // === Vorhersage ===
  const predictions = useMemo(() => {
    if (!settings.predictionsEnabled) return [];
    const prefix = getCurrentWordPrefix(text, cursor);
    if (!prefix) return [];
    const sentenceStart = settings.autoCapSentence && isSentenceStart(text, cursor);
    const extraWords = topics.flatMap((t) => t.words);
    return predictWords(prefix, { sentenceStart, extraWords, max: 6 });
  }, [text, cursor, topics, settings.predictionsEnabled, settings.autoCapSentence]);

  const topicWords = useMemo(() => {
    if (!activeTopic) return topics[0]?.words ?? [];
    return topics.find((t) => t.id === activeTopic)?.words ?? [];
  }, [topics, activeTopic]);

  function insertPrediction(word: string) {
    const editor = editorRef.current;
    if (!editor) return;
    const prefix = getCurrentWordPrefix(text, cursor);
    const before = text.slice(0, cursor).slice(0, -prefix.length);
    const after = text.slice(cursor);
    const newText = `${before}${word} ${after}`;
    const newCursor = before.length + word.length + 1;
    setText(newText);
    setCursor(newCursor);
    pendingCaretRef.current = newCursor;
    editor.focus();
    speak(word, { short: true });
  }

  function insertTopicWord(word: string) {
    const editor = editorRef.current;
    if (!editor) return;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const sep = before.length && !/\s$/.test(before) ? ' ' : '';
    const insertion = `${sep}${word} `;
    const newText = before + insertion + after;
    const newCursor = before.length + insertion.length;
    setText(newText);
    setCursor(newCursor);
    pendingCaretRef.current = newCursor;
    editor.focus();
    speak(word, { short: true });
  }

  function speakLast(kind: 'word' | 'sentence' | 'all') {
    if (!text.trim()) return;
    if (kind === 'all') return speak(text);
    if (kind === 'word') {
      const last = text.trim().split(/\s+/).pop();
      if (last) speak(last);
      return;
    }
    const sentences = text.match(/[^.!?]+[.!?]?/g);
    const last = sentences?.[sentences.length - 1];
    if (last) speak(last.trim());
  }

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Tab' && predictions.length > 0) {
        e.preventDefault();
        insertPrediction(predictions[0]!);
      }
    }
    editor.addEventListener('keydown', onKey);
    return () => editor.removeEventListener('keydown', onKey);
  }, [predictions]);

  if (!activeProfileId) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-bold text-gray-700">Kein Profil ausgewählt</p>
          <p className="mt-2 text-sm text-gray-500">
            Öffne den Lehrer-Bereich und wähle ein aktives Schüler:innen-Profil aus.
          </p>
          <Button asChild className="mt-4">
            <Link to="/lehrer">Zum Lehrer-Bereich</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-sky-bottom">
      {/* Topbar */}
      <header className="flex h-16 items-center gap-2 border-b-2 border-primary-soft bg-white px-3">
        <Button asChild variant="ghost" size="icon" aria-label="Zurück">
          <Link to="/">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-xl">
          ✏️
        </div>
        <div className="flex-1">
          <h1 className="text-base font-bold text-primary">Schreiben</h1>
          <p className="text-xs text-gray-500">
            {saver.isPending
              ? 'speichere…'
              : saver.lastSaved
                ? `gespeichert ${saver.lastSaved.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`
                : 'Tippe los!'}
          </p>
        </div>

        <Button variant="success" size="sm" onClick={() => speakLast('word')}>
          <Volume2 className="h-4 w-4" /> Wort
        </Button>
        <Button variant="success" size="sm" onClick={() => speakLast('sentence')}>
          <Volume2 className="h-4 w-4" /> Satz
        </Button>
        <Button variant="success" size="sm" onClick={() => speakLast('all')}>
          <Volume2 className="h-4 w-4" /> Alles
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void saver.saveNow()}
          disabled={saver.isPending || !text.trim()}
        >
          {saver.justSaved && !saver.isPending ? (
            <Check className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}{' '}
          Speichern
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={printWithTip}
        >
          <Printer className="h-4 w-4" /> Drucken
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={async () => {
            const lose = saver.isDirty && !settings.autoSave;
            const ok = confirm(
              lose
                ? 'Neuen Text beginnen? Der aktuelle Text ist noch NICHT gespeichert und geht verloren.'
                : 'Neuen Text beginnen? Der bisherige Text bleibt gespeichert.',
            );
            if (ok) {
              if (saver.isDirty && settings.autoSave) {
                // Nur weitermachen, wenn das Speichern wirklich geklappt hat —
                // sonst bliebe der Text weder am Server noch auf dem Gerät.
                const saved = await saver.saveNow();
                if (!saved) return;
              }
              saver.reset();
              setText('');
              setCursor(0);
              setDocId(null);
              saver.setLastSaved(null);
              navigate({ to: '/schreiben', search: { doc: undefined } }).catch(() => {});
            }
          }}
        >
          <Trash2 className="h-4 w-4" /> Neu
        </Button>
      </header>

      {/* Editor */}
      <main className="flex flex-1 flex-col gap-3 p-4">
        <PrintHeader section="Schreiben" />
        {loadedDoc?.corrections && docId === loadedDoc.id && (
          <CorrectionNote content={loadedDoc.content} corrections={loadedDoc.corrections} />
        )}
        <textarea
          ref={editorRef}
          value={text}
          onChange={(e) => {
            const raw = e.target.value;
            const caret = e.target.selectionStart;
            const next = settings.autoCapSentence ? autoCapitalize(text, raw) : raw;
            // Wenn wir ein Zeichen vor dem Cursor verändert haben, bleibt der Cursor
            // an seiner relativen Stelle — wir haben nur den Buchstaben groß gemacht,
            // nicht die Länge verändert.
            setText(next);
            setCursor(caret);
            // Cursor nach dem Render SYNCHRON zurücksetzen (useLayoutEffect unten).
            // Früher per setTimeout — bei schnellem Tippen oder Scribble sprang der
            // Cursor dann an eine alte Stelle und Buchstaben landeten falsch. (QC 19.10.2026)
            if (next !== raw) pendingCaretRef.current = caret;
          }}
          onSelect={(e) => setCursor((e.target as HTMLTextAreaElement).selectionStart)}
          onClick={(e) => setCursor((e.target as HTMLTextAreaElement).selectionStart)}
          placeholder="Hier tippen…"
          autoComplete="off"
          autoCapitalize="sentences"
          spellCheck
          lang="de-AT"
          className="flex-1 resize-none rounded-2xl border-4 border-clevy-vowel-soft bg-white p-6 outline-none focus:border-clevy-consonant print:rounded-none print:border-0 print:border-l-4 print:border-clevy-vowel-soft print:p-2 print:shadow-none"
          style={{
            fontSize: `${settings.fontSize}px`,
            lineHeight: 1.5,
            fontWeight: 500,
            letterSpacing: '0.3px',
          }}
        />

        {/* Vorschläge — beim Druck ausblenden, gehören nicht aufs Blatt */}
        <div className="flex min-h-[76px] flex-wrap items-center gap-2 rounded-2xl bg-white p-3 shadow-sm print:hidden">
          <span className="mr-2 text-sm font-bold text-gray-500">Vorschläge:</span>
          {predictions.length === 0 ? (
            <span className="rounded-xl border-2 border-dashed border-gray-300 px-4 py-2 text-sm text-gray-400">
              Tippe einen Buchstaben…
            </span>
          ) : (
            predictions.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => insertPrediction(w)}
                className="rounded-xl border-2 border-clevy-consonant bg-clevy-consonant-soft px-5 py-2.5 text-lg font-bold text-clevy-consonant transition-all hover:-translate-y-0.5 hover:bg-clevy-consonant hover:text-white"
              >
                {w}
              </button>
            ))
          )}
        </div>

        {/* Themen — beim Druck ausblenden */}
        {topics.length > 0 && (
          <div className="rounded-2xl bg-white p-3 shadow-sm print:hidden">
            <div className="mb-2 flex flex-wrap gap-1.5 border-b border-gray-200 pb-2">
              {topics.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTopic(t.id)}
                  className={cn(
                    'rounded-full border-2 px-4 py-1 text-sm font-bold transition-all',
                    (activeTopic ?? topics[0]?.id) === t.id
                      ? 'border-success bg-success text-white'
                      : 'border-gray-300 bg-white text-gray-700',
                  )}
                >
                  {t.name}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {topicWords.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => insertTopicWord(w)}
                  className="rounded-xl border-2 border-success bg-success-soft px-3.5 py-1.5 text-base font-bold text-success transition-all hover:bg-success hover:text-white"
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
